import { httpApi } from '@api/http.api.ts';
import type { FleetStatistics } from '@routes/sidebar/admin-center/___shared___/dashboards/types.ts';
import type {
    CommonTableRequest,
    Pathway,
    PathwaySummary,
    PathwayWatchlistRequest,
    StatisticsParams,
} from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { EmptyResultsDescription, PaginatedResponse } from '@types';

export type FleetStatisticsRequest = CommonTableRequest;

export interface GetFleetStatisticsParams {
    tenantAlias: string;
    payload: FleetStatisticsRequest;
}

export interface VesselSummary {
    vesselId: number;
    vesselName: string;
    vesselCrewCount: number;
    vesselCountryCode: string;

    requiredForCount: number;

    completedCount: number;
    dueLaterCount: number;
    dueSoonCount: number;
    overdueCount: number;
}
export interface PathwayWatchlistParams extends StatisticsParams {
    payload: PathwayWatchlistRequest;
}

export interface PathwayWatchlistVesselSummaryParams extends StatisticsParams {
    pathwayId: number;
    payload: PathwayWatchlistRequest;
}

export interface PathwayToWatchlistParams extends StatisticsParams {
    pathwayId: number;
}

export interface WatchlistPathwayOrderParams extends StatisticsParams {
    pathwayId: number;
    order: number;
    isDeleted: boolean;
}

const getStatisticsBaseUrl = (tenantAlias: string) => `admin-center/tenants/${tenantAlias}/statistics/fleet`;

const getWatchlistFleetBaseUrl = (tenantAlias: string) =>
    `admin-center/tenants/${tenantAlias}/statistics/watchlists/fleets`;

export const getFleetStatistics = ({ tenantAlias, payload }: GetFleetStatisticsParams): Promise<FleetStatistics> =>
    httpApi.post<FleetStatistics>(`${getStatisticsBaseUrl(tenantAlias)}/search`, payload).then(({ data }) => data);

export const getApiPathwayWatchlist = ({
    tenantAlias,
    payload,
}: PathwayWatchlistParams): Promise<PaginatedResponse<PathwaySummary, EmptyResultsDescription>> =>
    httpApi
        .post<
            PaginatedResponse<PathwaySummary, EmptyResultsDescription>
        >(`${getWatchlistFleetBaseUrl(tenantAlias)}/search`, payload)
        .then(({ data }) => data);

export const getApiPathwayWatchlistVesselSummary = ({
    tenantAlias,
    pathwayId,
    payload,
}: PathwayWatchlistVesselSummaryParams): Promise<PaginatedResponse<VesselSummary, number>> =>
    httpApi
        .post<
            PaginatedResponse<VesselSummary, number>
        >(`${getWatchlistFleetBaseUrl(tenantAlias)}/pathways/${pathwayId}/vessel-summaries/search`, payload)
        .then(({ data }) => data);

export const getApiPathways = ({ tenantAlias }: StatisticsParams): Promise<Pathway[]> =>
    httpApi.get<Pathway[]>(`${getWatchlistFleetBaseUrl(tenantAlias)}/pathways`).then(({ data }) => data);

export const postApiPathwayToWatchlist = ({ tenantAlias, pathwayId }: PathwayToWatchlistParams): Promise<any> =>
    httpApi
        .post(`${getWatchlistFleetBaseUrl(tenantAlias)}`, {
            pathwayId,
        })
        .then(({ data }) => data);

export const updateApiWatchlistPathwayOrder = ({
    tenantAlias,
    pathwayId,
    order,
    isDeleted,
}: WatchlistPathwayOrderParams): Promise<any> =>
    httpApi
        .put(`${getWatchlistFleetBaseUrl(tenantAlias)}`, {
            pathwayId,
            order,
            isDeleted,
        })
        .then(({ data }) => data);
